import { SLEEP_STAGE_COLORS } from "./stagePalette";
import type { DisplayChannel } from "./channelDisplay";

export type PresentationProfileId = "position" | "sleep-stage" | "percentage" | "battery" | "light" | "numeric";
export type PositionRepresentation = "quadrants" | "angle" | "both";
export type PresentationStyle = "trace" | "bands";
export const QUADRANTS = ["Supine", "Right lateral", "Prone", "Left lateral"] as const;
export const QUADRANT_COLOURS = ["#247ba0", "#f2b134", "#b65b88", "#52a889"] as const;
export const BATTERY_LABELS = ["Critical", "Low", "Medium", "High"] as const;
export const BATTERY_COLOURS = ["#bd4b50", "#c38a2b", "#628b9d", "#398571"] as const;
export interface PresentationProfile {
  id: PresentationProfileId;
  representations: readonly string[];
  defaultRepresentation: string;
  defaultStyle: PresentationStyle;
  unit?: string;
  scale?: readonly [number, number];
  colours?: readonly string[];
  categories?: readonly string[];
}
export const PRESENTATION_PROFILES: Record<PresentationProfileId, PresentationProfile> = {
  position: {id:"position", representations:["quadrants","angle","both"],defaultRepresentation:"quadrants",defaultStyle:"bands",unit:"°",scale:[0,360],colours:QUADRANT_COLOURS,categories:QUADRANTS},
  "sleep-stage": {id:"sleep-stage",representations:["bands","hypnogram"],defaultRepresentation:"bands",defaultStyle:"bands",categories:Object.keys(SLEEP_STAGE_COLORS),colours:Object.values(SLEEP_STAGE_COLORS)},
  percentage: {id:"percentage",representations:["numeric"],defaultRepresentation:"numeric",defaultStyle:"trace",unit:"%",scale:[0,100]},
  battery: {id:"battery",representations:["numeric","categories"],defaultRepresentation:"numeric",defaultStyle:"trace",unit:"%",scale:[0,100],colours:BATTERY_COLOURS,categories:BATTERY_LABELS},
  light: {id:"light",representations:["numeric"],defaultRepresentation:"numeric",defaultStyle:"trace",colours:["#ffd400"]},
  numeric: {id:"numeric",representations:["numeric"],defaultRepresentation:"numeric",defaultStyle:"trace"},
};
/** Stable semantic IDs/units only. Editable names are intentionally excluded. */
export function presentationProfile(channel: Pick<DisplayChannel,"modalityId"|"unit">): PresentationProfile {
  const id=channel.modalityId.toLowerCase();
  const semantic=id.split(".").at(-1);
  if(id.startsWith("position:"))return PRESENTATION_PROFILES.position;
  if(id === "sleep_stage")return PRESENTATION_PROFILES["sleep-stage"];
  if(semantic === "battery" || semantic === "battery_percentage")return PRESENTATION_PROFILES.battery;
  if(id === "ambient_light" || semantic === "light" || semantic === "illuminance")return PRESENTATION_PROFILES.light;
  if(channel.unit === "%" || semantic === "spo2")return PRESENTATION_PROFILES.percentage;
  return PRESENTATION_PROFILES.numeric;
}
/** Nearest quadrant centre; clockwise boundary belongs to the following quadrant. */
export function quadrantAt(angle: number): number | null {
  return Number.isFinite(angle) && angle>=0 && angle<360 ? Math.floor((angle+45)/90)%4 : null;
}
export function batteryCategory(value: number | null): number | null {
  if(value === null || !Number.isFinite(value) || value<0 || value>100)return null;
  return value<=5?0:value<=25?1:value<=75?2:3;
}
/** Cyclic interpolation through the same anchors used by quadrant bands and steps. */
export function angleColour(angle:number):string {
  const sector=((angle%360)+360)%360/90,index=Math.floor(sector),mix=sector-index;
  const rgb=(hex:string)=>[1,3,5].map(start=>parseInt(hex.slice(start,start+2),16));
  const a=rgb(QUADRANT_COLOURS[index]),b=rgb(QUADRANT_COLOURS[(index+1)%4]);
  return `rgb(${a.map((v,i)=>Math.round(v+(b[i]-v)*mix)).join(",")})`;
}
